
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useStatusManagement = (
  coachRecommendations: any[],
  setCoachRecommendations: (recommendations: any[]) => void,
  trialSessions: any[],
  setTrialSessions: (trials: any[]) => void
) => {
  const { toast } = useToast();
  const [statusChangeNotes, setStatusChangeNotes] = useState("");
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showTrialStatusDialog, setShowTrialStatusDialog] = useState(false);
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [statusHistory, setStatusHistory] = useState<any[]>([]);
  const [selectedRecommendation, setSelectedRecommendation] = useState<any>(null);
  const [selectedTrialSession, setSelectedTrialSession] = useState<any>(null);
  const [newStatus, setNewStatus] = useState<string>("");

  const handleStatusChange = async () => {
    if (!selectedRecommendation || !newStatus) return;

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError) throw userError;

      // First update the recommendation status
      const { error: updateError } = await supabase
        .from('coach_recommendations')
        .update({ status: newStatus })
        .eq('id', selectedRecommendation.id);

      if (updateError) throw updateError;

      // Then record the status change
      const { error: historyError } = await supabase
        .from('coach_recommendation_status_changes')
        .insert({
          recommendation_id: selectedRecommendation.id,
          old_status: selectedRecommendation.status,
          new_status: newStatus,
          changed_by: userData.id,
          notes: statusChangeNotes,
        });

      if (historyError) throw historyError;

      setCoachRecommendations(
        coachRecommendations.map(rec =>
          rec.id === selectedRecommendation.id
            ? { ...rec, status: newStatus }
            : rec
        )
      );

      toast({
        title: "Başarılı",
        description: "Durum güncellendi.",
      });

      setShowStatusDialog(false);
      setStatusChangeNotes("");
      setNewStatus("");
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Durum güncellenirken bir hata oluştu.",
      });
    }
  };

  const handleTrialStatusChange = async () => {
    if (!selectedTrialSession || !newStatus) return;

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError) throw userError;

      // First update the trial session status
      const { error: updateError } = await supabase
        .from('trial_sessions')
        .update({ status: newStatus })
        .eq('id', selectedTrialSession.id);

      if (updateError) throw updateError;

      // Then record the status change
      const { error: historyError } = await supabase
        .from('trial_session_status_changes')
        .insert({
          trial_session_id: selectedTrialSession.id,
          old_status: selectedTrialSession.status,
          new_status: newStatus,
          changed_by: userData.id,
          notes: statusChangeNotes
        });

      if (historyError) throw historyError;

      setTrialSessions(
        trialSessions.map(trial =>
          trial.id === selectedTrialSession.id
            ? { ...trial, status: newStatus }
            : trial
        )
      );

      toast({
        title: "Başarılı",
        description: "Durum güncellendi.",
      });

      setShowTrialStatusDialog(false);
      setStatusChangeNotes("");
      setNewStatus("");
      setSelectedTrialSession(null);
    } catch (error) {
      console.error('Error updating trial status:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Durum güncellenirken bir hata oluştu.",
      });
    }
  };

  const fetchStatusHistory = async (recommendationId: string) => {
    try {
      const { data, error } = await supabase
        .from('coach_recommendation_status_changes')
        .select(`
          *,
          changed_by:users!changed_by(email)
        `)
        .eq('recommendation_id', recommendationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setStatusHistory(data || []);
      setShowStatusHistory(true);
    } catch (error) {
      console.error('Error fetching status history:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Durum geçmişi yüklenirken bir hata oluştu.",
      });
    }
  };

  const fetchTrialStatusHistory = async (trialSessionId: string) => {
    try {
      const { data, error } = await supabase
        .from('trial_session_status_changes')
        .select(`
          *,
          changed_by:users!changed_by(email)
        `)
        .eq('trial_session_id', trialSessionId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setStatusHistory(data || []);
      setShowStatusHistory(true);
    } catch (error) {
      console.error('Error fetching trial status history:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Durum geçmişi yüklenirken bir hata oluştu.",
      });
    }
  };

  return {
    statusChangeNotes,
    setStatusChangeNotes,
    showStatusDialog,
    setShowStatusDialog,
    showTrialStatusDialog,
    setShowTrialStatusDialog,
    showStatusHistory,
    setShowStatusHistory,
    statusHistory,
    selectedRecommendation,
    setSelectedRecommendation,
    selectedTrialSession,
    setSelectedTrialSession,
    newStatus,
    setNewStatus,
    handleStatusChange,
    handleTrialStatusChange,
    fetchStatusHistory,
    fetchTrialStatusHistory,
  };
};
